import React, { useState, useEffect, useRef } from "react";
import "./FeedbackForm.css";
import { usePostFeedbackRequestMutation } from "../../slices/feedbackSlice";
import { useGetAcademyQuery } from "../../slices/userApiSlice";
import { toast } from "sonner";
import { FaThumbsUp, FaThumbsDown } from "react-icons/fa";

const COOLDOWN_STORAGE_KEY = "feedbackCooldownUntil";
const SUBMISSION_WINDOW_STORAGE_KEY = "feedbackSubmissionWindow";
const DAILY_LIMIT_SECONDS = 24 * 60 * 60;
const DAILY_LIMIT_COUNT = 1;
const RATE_LIMIT_DISABLED = false;

const FeedbackForm = () => {
  const [academy, setAcademy] = useState("All Academy");
  const {
    data: academies,
    isLoading: isAcademyLoading,
    isError: isAcademyError,
  } = useGetAcademyQuery();
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [reviewType, setReviewType] = useState(null);
  const [hoveredReviewType, setHoveredReviewType] = useState(null);
  const [errors, setErrors] = useState({});
  const [postFeedback, { isLoading, isSuccess, isError, error }] =
    usePostFeedbackRequestMutation();
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(0);
  const [submissionWindow, setSubmissionWindow] = useState(null);

  const selectRef = useRef(null);
  const [showAcademyList, setShowAcademyList] = useState(false);

  useEffect(() => {
    if (RATE_LIMIT_DISABLED) {
      setCooldownUntil(null);
      setCooldownSecondsLeft(0);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(COOLDOWN_STORAGE_KEY);
      }
      return;
    }
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(COOLDOWN_STORAGE_KEY);
    if (!stored) return;
    const until = Number(stored);
    if (Number.isFinite(until) && until > Date.now()) {
      setCooldownUntil(until);
      setCooldownSecondsLeft(Math.ceil((until - Date.now()) / 1000));
    } else {
      window.localStorage.removeItem(COOLDOWN_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (RATE_LIMIT_DISABLED) return;
    if (typeof window === "undefined") return;
    const onStorage = (event) => {
      if (event.key !== COOLDOWN_STORAGE_KEY) return;
      const next = event.newValue ? Number(event.newValue) : null;
      if (next && next > Date.now()) {
        setCooldownUntil(next);
        setCooldownSecondsLeft(Math.ceil((next - Date.now()) / 1000));
      } else {
        setCooldownUntil(null);
        setCooldownSecondsLeft(0);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (RATE_LIMIT_DISABLED) return;
    if (!cooldownUntil || typeof window === "undefined") return;
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((cooldownUntil - Date.now()) / 1000),
      );
      setCooldownSecondsLeft(remaining);
      if (remaining <= 0) {
        setCooldownUntil(null);
        if (typeof window !== "undefined") {
          window.localStorage.removeItem(COOLDOWN_STORAGE_KEY);
        }
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const applyCooldown = (seconds) => {
    if (RATE_LIMIT_DISABLED) return;
    const safeSeconds = Math.max(1, Number(seconds) || 0);
    const until = Date.now() + safeSeconds * 1000;
    setCooldownUntil(until);
    setCooldownSecondsLeft(safeSeconds);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COOLDOWN_STORAGE_KEY, String(until));
    }
  };

  useEffect(() => {
    if (RATE_LIMIT_DISABLED) return;
    if (typeof window === "undefined") return;
    const rawWindow = window.localStorage.getItem(
      SUBMISSION_WINDOW_STORAGE_KEY,
    );
    if (!rawWindow) return;

    try {
      const parsed = JSON.parse(rawWindow);
      if (
        !parsed ||
        typeof parsed.windowStart !== "number" ||
        typeof parsed.count !== "number"
      ) {
        window.localStorage.removeItem(SUBMISSION_WINDOW_STORAGE_KEY);
        return;
      }

      const elapsedMs = Date.now() - parsed.windowStart;
      if (elapsedMs >= DAILY_LIMIT_SECONDS * 1000) {
        window.localStorage.removeItem(SUBMISSION_WINDOW_STORAGE_KEY);
        setSubmissionWindow(null);
        return;
      }

      setSubmissionWindow(parsed);

      if (parsed.count >= DAILY_LIMIT_COUNT) {
        const remainingSeconds = Math.ceil(
          (DAILY_LIMIT_SECONDS * 1000 - elapsedMs) / 1000,
        );
        const nextUntil = Date.now() + remainingSeconds * 1000;
        setCooldownUntil(nextUntil);
        setCooldownSecondsLeft(remainingSeconds);
        window.localStorage.setItem(COOLDOWN_STORAGE_KEY, String(nextUntil));
      }
    } catch (err) {
      console.error("Failed to parse submission window", err);
      window.localStorage.removeItem(SUBMISSION_WINDOW_STORAGE_KEY);
    }
  }, []);

  // close academy dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) {
        setShowAcademyList(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // Handle review button click
  const handleReviewChange = (type) => {
    setReviewType(type);
    setErrors((prev) => ({ ...prev, reviewType: "" }));
  };

  // Handle academy change
  const handleAcademyChange = (e) => {
    setAcademy(e.target.value);
    if (errors.academy) setErrors((prev) => ({ ...prev, academy: "" }));
  };

  // Handle subject change
  const handleSubjectChange = (e) => {
    setSubject(e.target.value);
    if (errors.subject) setErrors((prev) => ({ ...prev, subject: "" }));
  };

  // Handle description change
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    if (errors.description) setErrors((prev) => ({ ...prev, description: "" }));
  };

  const extractRetrySeconds = (err) => {
    if (
      err?.data?.retryAfterSeconds &&
      Number(err.data.retryAfterSeconds) > 0
    ) {
      return Number(err.data.retryAfterSeconds);
    }
    const headerVal =
      err?.meta?.response?.headers?.get?.("retry-after") ||
      err?.headers?.get?.("retry-after");
    const headerSeconds = Number(headerVal);
    if (Number.isFinite(headerSeconds) && headerSeconds > 0)
      return headerSeconds;

    const message =
      err?.data?.message ||
      (typeof err?.data === "string" ? err.data : null) ||
      err?.error?.data?.message ||
      err?.error;

    if (typeof message === "string") {
      const numericMatch = message.match(/(\d+(?:\.\d+)*)\s*seconds?/i);
      if (numericMatch && Number(numericMatch[1]) > 0) {
        return Number(numericMatch[1]);
      }
      const genericNumber = message.match(/(\d+(?:\.\d+)?)/);
      if (genericNumber && Number(genericNumber[1]) > 0) {
        return Number(genericNumber[1]);
      }
    }
    return 60;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!RATE_LIMIT_DISABLED && cooldownSecondsLeft > 0) {
      toast.info("Daily feedback limit reached", {
        description: `You can submit up to ${DAILY_LIMIT_COUNT} feedback entries per day. Please try again in ${formatTime(cooldownSecondsLeft)}.`,
      });
      return;
    }

    let newErrors = {};

    if (academy === "All Academy") newErrors.academy = "Select an academy.";
    if (!subject.trim()) newErrors.subject = "Enter a subject.";
    if (!description.trim()) newErrors.description = "Enter the description.";
    if (!reviewType) newErrors.reviewType = "Select feedback type.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const feedbackData = { academy, subject, description, reviewType };

    // (client-side rate limiting removed) proceed to confirmation

    toast("Are you sure?", {
      description: "Do you want to submit this feedback?",
      action: {
        label: "Yes, submit!",
        onClick: async () => {
          try {
            const res = await postFeedback(feedbackData).unwrap();

            toast.success("You have successfully submitted feedback!");

            // Reset form
            setAcademy("All Academy");
            setSubject("");
            setDescription("");
            setReviewType(null);
            setErrors({});

            if (!RATE_LIMIT_DISABLED) {
              // ...existing logic for rate limiting...
              const nowTs = Date.now();
              let currentWindow = submissionWindow;
              const windowMs = DAILY_LIMIT_SECONDS * 1000;

              if (
                !currentWindow ||
                nowTs - currentWindow.windowStart >= windowMs
              ) {
                currentWindow = { windowStart: nowTs, count: 0 };
              }

              const updatedWindow = {
                windowStart: currentWindow.windowStart,
                count: Math.min(DAILY_LIMIT_COUNT, currentWindow.count + 1),
              };

              setSubmissionWindow(updatedWindow);
              if (typeof window !== "undefined") {
                window.localStorage.setItem(
                  SUBMISSION_WINDOW_STORAGE_KEY,
                  JSON.stringify(updatedWindow),
                );
              }

              const backendCooldownSeconds =
                Number(res?.retryAfterSeconds) ||
                Number(res?.cooldownSeconds) ||
                Number(res?.remainingCooldownSeconds) ||
                Number(res?.data?.retryAfterSeconds);

              let enforcedCooldownSeconds = 0;

              if (updatedWindow.count >= DAILY_LIMIT_COUNT) {
                const elapsedMs = nowTs - updatedWindow.windowStart;
                enforcedCooldownSeconds = Math.max(
                  enforcedCooldownSeconds,
                  Math.ceil((windowMs - elapsedMs) / 1000),
                );
              }

              if (
                Number.isFinite(backendCooldownSeconds) &&
                backendCooldownSeconds > 0
              ) {
                enforcedCooldownSeconds = Math.max(
                  enforcedCooldownSeconds,
                  backendCooldownSeconds,
                );
              }

              if (enforcedCooldownSeconds > 0) {
                applyCooldown(enforcedCooldownSeconds);
              } else {
                setCooldownUntil(null);
                setCooldownSecondsLeft(0);
                if (typeof window !== "undefined") {
                  window.localStorage.removeItem(COOLDOWN_STORAGE_KEY);
                }
              }
            } else {
              setCooldownUntil(null);
              setCooldownSecondsLeft(0);
            }
          } catch (err) {
            console.error("Feedback submission failed", err);
            // Normalize error shapes returned by RTK Query / fetchBaseQuery
            let status =
              err?.status ||
              err?.error?.status ||
              err?.originalStatus ||
              (err?.data && err.data.status);
            let errorMsg =
              err?.data?.message ||
              (typeof err?.data === "string" ? err.data : null) ||
              err?.error?.data?.message ||
              err?.message ||
              "Please try again.";

            const looksLikeRateLimit =
              status === 429 ||
              (typeof errorMsg === "string" &&
                /rate limit|too many request/i.test(errorMsg));

            if (looksLikeRateLimit) {
              const waitSeconds = extractRetrySeconds(err);
              applyCooldown(waitSeconds);
              toast.info("Rate limit reached", {
                description:
                  typeof errorMsg === "string" && errorMsg.trim().length > 0
                    ? `${errorMsg} You can submit again in ${formatTime(waitSeconds)}.`
                    : `Please wait ${formatTime(waitSeconds)} before submitting another feedback.`,
              });
              return;
            }

            // generic error popup
            toast.error("Failed to submit feedback", {
              description: errorMsg,
            });
          }
        },
      },
    });
  };

  // Format seconds into HH:MM:SS or MM:SS
  const formatTime = (secs) => {
    const s = Math.max(0, Math.floor(Number(secs) || 0));
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Responsive helpers
  const isMobile = typeof window !== "undefined" && window.innerWidth < 500;

  return (
    <div className="fb-section-container">
      <div className="fb-header">
        <h2 className="fb-main-title">Share Your Feedback</h2>
        <div className="fb-title-accent">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <p className="fb-description">
          Your insights help us improve the Facility Management System. Please
          let us know about your experience or suggest any improvements.
        </p>
      </div>

      <div className="fb-card">
        <div className="fb-form-wrapper">
          <form onSubmit={handleSubmit} className="fb-modern-form">
            {/* Review Type */}
            <div className="fb-review-section">
              <p className="fb-review-label">How was your experience?</p>
              <div className="fb-review-buttons">
                <div
                  className={`fb-review-option ${
                    hoveredReviewType === "positive" ? "hovered" : ""
                  }`}
                  onMouseEnter={() => setHoveredReviewType("positive")}
                  onMouseLeave={() => setHoveredReviewType(null)}
                >
                  <span
                    className={`fb-review-text ${
                      hoveredReviewType === "positive" ? "visible" : ""
                    }`}
                  >
                    Positive
                  </span>
                  <button
                    type="button"
                    onClick={() => handleReviewChange("positive")}
                    className={`fb-thumb-btn thumb-up ${
                      reviewType === "positive" ? "selected" : ""
                    } ${errors.reviewType && !reviewType ? "error-pulse" : ""}`}
                  >
                    <FaThumbsUp />
                  </button>
                </div>

                <div
                  className={`fb-review-option ${
                    hoveredReviewType === "negative" ? "hovered" : ""
                  }`}
                  onMouseEnter={() => setHoveredReviewType("negative")}
                  onMouseLeave={() => setHoveredReviewType(null)}
                >
                  <button
                    type="button"
                    onClick={() => handleReviewChange("negative")}
                    className={`fb-thumb-btn thumb-down ${
                      reviewType === "negative" ? "selected" : ""
                    } ${errors.reviewType && !reviewType ? "error-pulse" : ""}`}
                  >
                    <FaThumbsDown />
                  </button>
                  <span
                    className={`fb-review-text ${
                      hoveredReviewType === "negative" ? "visible" : ""
                    }`}
                  >
                    Negative
                  </span>
                </div>
              </div>
              {errors.reviewType && (
                <p className="fb-error-msg">{errors.reviewType}</p>
              )}
            </div>

            <div className="fb-fields-grid">
              {/* Academy */}
              <div className="fb-field-box">
                <label className="fb-label">Academy</label>
                <div
                  ref={selectRef}
                  className={`fb-custom-select ${
                    errors.academy ? "error" : ""
                  }`}
                  onClick={() => {
                    if (!isAcademyLoading) setShowAcademyList((s) => !s);
                  }}
                >
                  <div className="fb-select-trigger">
                    <span>
                      {(() => {
                        if (academy === "All Academy") return "All Academy";
                        const found =
                          academies &&
                          academies.find(
                            (a) => String(a.academyId) === String(academy),
                          );
                        return found ? found.name : academy;
                      })()}
                    </span>
                    <span
                      className={`fb-select-arrow ${
                        showAcademyList ? "open" : ""
                      }`}
                    >
                      ▾
                    </span>
                  </div>

                  {showAcademyList && !isAcademyLoading && (
                    <ul className="fb-select-options">
                      <li
                        className={`fb-select-option ${
                          academy === "All Academy" ? "selected" : ""
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAcademy("All Academy");
                          setShowAcademyList(false);
                        }}
                      >
                        All Academy
                      </li>
                      {academies &&
                        Array.isArray(academies) &&
                        academies.map((a) => (
                          <li
                            key={a.id || a._id || a.name}
                            className={`fb-select-option ${
                              String(a.academyId) === String(academy)
                                ? "selected"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAcademy(a.academyId);
                              setShowAcademyList(false);
                            }}
                          >
                            {a.name}
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
                {errors.academy && (
                  <p className="fb-error-msg">{errors.academy}</p>
                )}
              </div>

              {/* Subject */}
              <div className="fb-field-box">
                <label className="fb-label">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={handleSubjectChange}
                  placeholder="Feedback Title"
                  className={`fb-input ${errors.subject ? "error" : ""}`}
                />
                {errors.subject && (
                  <p className="fb-error-msg">{errors.subject}</p>
                )}
              </div>

              {/* Description */}
              <div className="fb-field-box full">
                <label className="fb-label">Description</label>
                <textarea
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Tell us more about your experience..."
                  className={`fb-input fb-textarea ${
                    errors.description ? "error" : ""
                  }`}
                />
                {errors.description && (
                  <p className="fb-error-msg">{errors.description}</p>
                )}
              </div>
            </div>

            {!RATE_LIMIT_DISABLED && cooldownSecondsLeft > 0 && (
              <div className="fb-cooldown-alert">
                You can send up to {DAILY_LIMIT_COUNT} feedback entries per day.
                Try again in {formatTime(cooldownSecondsLeft)}.
              </div>
            )}

            <div className="fb-form-actions">
              <button
                type="submit"
                className="fb-submit-btn"
                disabled={
                  isLoading || (!RATE_LIMIT_DISABLED && cooldownSecondsLeft > 0)
                }
              >
                {cooldownSecondsLeft > 0 ? (
                  `Available in ${formatTime(cooldownSecondsLeft)}`
                ) : isLoading ? (
                  <div className="fb-loader">
                    <span></span>Submitting...
                  </div>
                ) : (
                  "Submit Feedback"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
